import React, { useState } from 'react';
import { Heart, BookOpen, Star, ChevronDown, ChevronUp } from 'lucide-react';
import CatholicApiService from '../services/catholicApi';


const Prayers = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedPrayer, setExpandedPrayer] = useState(null);
  const prayers = CatholicApiService.getPrayers();

  const categories = [
    { id: 'all', name: 'Toutes les prières', icon: Heart },
    { id: 'Prières fondamentales', name: 'Prières fondamentales', icon: Star },
    { id: 'Prières mariales', name: 'Prières mariales', icon: Heart },
    { id: 'Prières trinitaires', name: 'Prières trinitaires', icon: BookOpen }
  ];

  const additionalPrayers = [
    {
      id: 4,
      title: "Angélus",
      text: "L'Ange du Seigneur porta l'annonce à Marie, et elle conçut du Saint-Esprit. Je vous salue Marie... Voici la servante du Seigneur, qu'il me soit fait selon votre parole. Je vous salue Marie... Et le Verbe s'est fait chair, et il a habité parmi nous. Je vous salue Marie... Priez pour nous, sainte Mère de Dieu, afin que nous soyons rendus dignes des promesses du Christ. Prions : Que votre grâce, Seigneur, se répande en nos cœurs. Par la message de l'Ange, nous avons connu l'Incarnation de votre Fils bien-aimé, conduisez-nous par sa Passion et par sa Croix jusqu'à la gloire de la Résurrection. Par le même Jésus, le Christ, notre Seigneur. Amen.",
      category: "Prières mariales"
    },
    {
      id: 5,
      title: "Acte de contrition",
      text: "Mon Dieu, j'ai un très grand regret de vous avoir offensé, parce que vous êtes infiniment bon, infiniment aimable, et que le péché vous déplaît. Je prends la ferme résolution, avec le secours de votre sainte grâce, de ne plus vous offenser et de faire pénitence. Amen.",
      category: "Prières fondamentales"
    },
    {
      id: 6,
      title: "Prière à Saint Joseph",
      text: "Ô saint Joseph, père adoptif de Jésus et époux de Marie, nous nous tournons vers vous avec confiance. Vous qui avez vécu dans l'intimité de la Sainte Famille, intercédez pour nos familles. Protégez les pères de famille, guidez les époux dans leur mission, et aidez-nous à vivre selon l'exemple du Christ. Saint Joseph, priez pour nous. Amen.",
      category: "Prières familiales"
    },
    {
      id: 7,
      title: "Chapelet - Mystères Joyeux",
      text: "1. L'Annonciation - L'Ange Gabriel annonce à Marie qu'elle sera la Mère de Dieu.\n2. La Visitation - Marie rend visite à sa cousine Élisabeth.\n3. La Nativité - Jésus naît à Bethléem.\n4. La Présentation - Jésus est présenté au Temple.\n5. Le Recouvrement - Jésus est retrouvé au Temple parmi les docteurs.",
      category: "Prières mariales"
    }
  ];

  const allPrayers = [...prayers, ...additionalPrayers];

  const filteredPrayers = selectedCategory === 'all' 
    ? allPrayers 
    : allPrayers.filter(prayer => prayer.category === selectedCategory);

  const togglePrayer = (prayerId) => {
    setExpandedPrayer(expandedPrayer === prayerId ? null : prayerId);
  };

  return (
    <>
      
    <section id="prieres" className="py-16 bg-gradient-to-br from-amber-50 to-blue-50">
    
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Section Prières
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez nos prières traditionnelles et approfondissez votre vie spirituelle 
            avec les trésors de la tradition catholique.
          </p>
        </div>

        {/* Catégories de prières */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-blue-50 shadow-md'
                }`}
              >
                <IconComponent className="w-5 h-5" />
                <span className="font-medium">{category.name}</span>
              </button>
            );
          })}
        </div>

        {/* Liste des prières */}
        <div className="max-w-4xl mx-auto space-y-6">
          {filteredPrayers.map((prayer) => (
            <div key={prayer.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => togglePrayer(prayer.id)}
                className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-amber-500 rounded-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{prayer.title}</h3>
                      <p className="text-blue-600 text-sm">{prayer.category}</p>
                    </div>
                  </div>
                  {expandedPrayer === prayer.id ? (
                    <ChevronUp className="w-6 h-6 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-500" />
                  )}
                </div>
              </button>

              {expandedPrayer === prayer.id && (
                <div className="px-6 pb-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {prayer.text}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex space-x-4">
                      <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200">
                        <BookOpen className="w-4 h-4" />
                        <span>Imprimer</span>
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200">
                        <Heart className="w-4 h-4" />
                        <span>Favoris</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Section prière du jour */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-amber-600 text-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">Prière du jour</h3>
            <div className="bg-white bg-opacity-10 rounded-lg p-6">
              <p className="text-lg italic leading-relaxed">
                "Seigneur Jésus, nous vous confions notre communauté des Fleurs de la Sainte Famille. 
                Que votre amour nous unisse comme vous avez uni Joseph et Marie. Aidez-nous à être 
                des témoins de votre paix et de votre joie dans le monde. Que la Sainte Famille 
                soit notre modèle et notre protection. Amen."
              </p>
            </div>
          </div>
        </div>

        {/* Horaires de prière */}
        <div className="mt-16 grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Horaires de prière quotidienne</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-700">Laudes</span>
                <span className="text-blue-600">7h00</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-700">Angélus</span>
                <span className="text-blue-600">12h00</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-700">Vêpres</span>
                <span className="text-blue-600">18h00</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="font-medium text-gray-700">Complies</span>
                <span className="text-blue-600">21h00</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Conseils pour la prière</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Choisissez un lieu calme et propice au recueillement</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Commencez par un moment de silence pour vous mettre en présence de Dieu</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Priez avec le cœur, pas seulement avec les lèvres</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>N'hésitez pas à intercéder pour les autres</span>
              </li>
            </ul>
          </div>
        </div>
      </div>     
    </section>
  
   </>
  );
};

export default Prayers;